import { Link } from "@tanstack/react-router";
import { Camera, Globe2, MapPin, Users, type LucideIcon } from "lucide-react";
import { PulseIndicator, usePulse, pulseLevelStyle } from "@/components/pulse-indicator";
import { cn } from "@/lib/utils";

/**
 * Shown when the Lens timeline has nothing to render. It stays useful: live
 * area signal, a clear first action, and quiet routes into the rest of Xamseen.
 */
export function LensEmptyState({
  place,
  onCompose,
  onSeeLatest,
}: {
  place?: string | null;
  onCompose?: () => void;
  onSeeLatest?: () => void;
}) {
  const pulse = usePulse(place);
  const tone = pulseLevelStyle(pulse.level);

  return (
    <div className="px-4 py-10 sm:py-14">
      <div className="mx-auto max-w-md text-center">
        <div className="flex justify-center">
          <PulseIndicator level={pulse.level} intensity={pulse.intensity} size={34} />
        </div>

        <h2 className="mt-5 font-display text-xl font-bold tracking-tight sm:text-2xl">
          {place ? `It's calm in ${place}` : "It's calm around you"}
        </h2>
        <p className={cn("mt-1.5 text-sm font-medium", tone.text)}>{pulse.detail}</p>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
          Nothing has been shared here yet. Post what you're seeing and your neighbours will pick it up.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {onCompose ? (
            <button
              onClick={onCompose}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              <Camera className="h-4 w-4" /> Share what you see
            </button>
          ) : (
            <Link
              to="/report"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              <Camera className="h-4 w-4" /> Share what you see
            </Link>
          )}
          {onSeeLatest && (
            <button
              onClick={onSeeLatest}
              className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:border-plum/50"
            >
              <Globe2 className="h-4 w-4" /> See the latest
            </button>
          )}
        </div>

        <div className="mt-8 grid gap-2 text-left sm:grid-cols-3">
          <Suggestion to="/map" icon={MapPin} label="Open the Map" hint="See reports near you" />
          <Suggestion to="/spaces" icon={Users} label="Join a Space" hint="Find your community" />
          <Suggestion to="/nearby" icon={Globe2} label="Explore nearby" hint="Widen your radius" />
        </div>
      </div>
    </div>
  );
}

function Suggestion({
  to,
  icon: Icon,
  label,
  hint,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  hint: string;
}) {
  return (
    <Link
      to={to}
      className="panel flex items-center gap-3 px-3.5 py-3 transition-colors hover:border-plum/50 sm:flex-col sm:items-start sm:gap-2"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted">
        <Icon className="h-4 w-4 text-plum" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{label}</span>
        <span className="block truncate text-xs text-muted-foreground">{hint}</span>
      </span>
    </Link>
  );
}
